import { useWatch } from 'react-hook-form';
import React, { useMemo } from 'react';
import type { DataTableRenderCellParams } from '@/components/custom/erp/data-table-v2';
import type { ErpInputTableActions } from '@/components/custom/erp';
import { FormFieldActions, FormFieldState } from '../../../hooks';
import { SelectedDetailCellInfo } from '../hooks/useDetailRows';
import { ErpInputTableV2 } from '@/components/custom/erp';
import { getTaxTableColumns } from './columns';
import { FormMode } from '@/types/form';

interface TaxTabProps {
  formMode: FormMode;
  rows: { uuid?: string | null }[];
  selectedRowUuid?: string | null;
  selectedCell?: SelectedDetailCellInfo | null;
  state: {
    state: FormFieldState;
    actions: FormFieldActions;
  };
  onRowClick?: (params: MuaHangRecord) => void;
  onCellClick?: (params: DataTableRenderCellParams) => void;
  onAddRow: () => void;
  onDeleteRow: () => void;
  onCopyRow: () => void;
  onPasteRow: () => void;
  onMoveRow: (direction: 'up' | 'down') => void;
  onCellValueChange: (rowUuid: string, field: string, newValue: MuaHangRecord) => void;
  onCellCommit?: (rowUuid: string, field: string, newValue: MuaHangRecord) => void;
  onHandleDetail?: () => void;
  isPasting?: boolean;
}

export const TaxTab: React.FC<TaxTabProps> = ({
  formMode,
  rows,
  selectedRowUuid,
  selectedCell,
  state,
  onRowClick,
  onCellClick,
  onAddRow,
  onDeleteRow,
  onCopyRow,
  onPasteRow,
  onMoveRow,
  onCellValueChange,
  onCellCommit,
  onHandleDetail,
  isPasting
}) => {
  const [dien_giai, so_ct0, so_ct2, ngay_ct0, ma_kh, ten_kh, dia_chi, ma_so_thue] = useWatch({
    name: ['dien_giai', 'so_ct0', 'so_ct2', 'ngay_ct0', 'ma_kh', 'ten_kh', 'dia_chi', 'ma_so_thue']
  });

  const columns = useMemo(
    () =>
      getTaxTableColumns({
        formMode,
        state: state.state,
        actions: state.actions,
        dien_giai,
        so_ct0,
        so_ct2,
        ngay_ct0,
        ma_kh,
        ten_kh,
        dia_chi,
        ma_so_thue,
        onCellValueChange,
        onCellCommit
      }),
    [
      dia_chi,
      dien_giai,
      formMode,
      ma_kh,
      ma_so_thue,
      ngay_ct0,
      onCellCommit,
      onCellValueChange,
      so_ct0,
      so_ct2,
      state.actions,
      state.state,
      ten_kh
    ]
  );

  const actions = useMemo<ErpInputTableActions>(
    () => ({
      detail: onHandleDetail,
      add: onAddRow,
      delete: onDeleteRow,
      copy: onCopyRow,
      paste: onPasteRow,
      moveUp: () => onMoveRow('up'),
      moveDown: () => onMoveRow('down')
    }),
    [onAddRow, onCopyRow, onDeleteRow, onHandleDetail, onMoveRow, onPasteRow]
  );

  return (
    <ErpInputTableV2
      rows={rows}
      autoHeight
      bottomSelector='[data-arito-bottom-bar]'
      onRowClick={onRowClick}
      onCellClick={onCellClick}
      selectedRowId={selectedRowUuid || undefined}
      columns={columns}
      getRowId={row => row?.uuid || ''}
      actions={actions}
      actionsDisabled={formMode === 'view'}
      actionLoading={isPasting}
      actionOrder={['detail', 'add', 'delete', 'copy', 'paste', 'moveUp', 'moveDown']}
      pasteTarget={{ id: selectedRowUuid || '', field: selectedCell?.field || '' }}
    />
  );
};
